import React from "react";
import { FormSection } from "../../../common/FormComponents";
import {
  Checkbox,
  FormControlLabel,
  Chip,
  TextField,
  MenuItem,
  Select,
  SelectChangeEvent,
  Autocomplete,
} from "@mui/material";

type Recipients = {
  sendTo: string[];
  includeMembers: boolean;
  includeNonMembers: boolean;
  includeEblastSubscribers: boolean;
  specificGroups: string[];
};

type RecipientSectionProps = {
  recipients: Recipients;
  onChange: ({
    field,
    value,
  }: {
    field: keyof Omit<Recipients, "sendTo" | "specificGroups">;
    value: boolean;
  }) => void;
  onGroupsChange: ({ groups }: { groups: string[] }) => void;
  errors?: Record<string, string>;
};

// Mock data for member groups
const memberGroups = [
  { id: "group1", name: "Board Members" },
  { id: "group2", name: "Committee Chairs" },
  { id: "group3", name: "New Members" },
  { id: "group4", name: "Event Sponsors" },
  { id: "group5", name: "Small Business" },
  { id: "group6", name: "Corporate Members" },
  { id: "group7", name: "Non-Profit Organizations" },
];

const RecipientSection = ({
  recipients,
  onChange,
  onGroupsChange,
  errors = {},
}: RecipientSectionProps) => {
  const handleCheckboxChange =
    (field: keyof Omit<Recipients, "sendTo" | "specificGroups">) =>
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange({ field, value: e.target.checked });
    };

  const handleGroupsChange = (_: any, value: string[]) => {
    onGroupsChange({ groups: value });
  };

  return (
    <FormSection
      title="Recipients"
      tooltipText="Select who should receive this email"
    >
      <div className="mb-6">
        <p className="text-sm font-medium text-gray-700 mb-2">Send to:</p>

        <div>
          <FormControlLabel
            control={
              <Checkbox
                checked={recipients.includeMembers}
                onChange={handleCheckboxChange("includeMembers")}
                color="primary"
              />
            }
            label="Chamber Members"
          />

          {/* <div className="ml-7 mt-1">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={recipients.includeNonMembers}
                    onChange={handleCheckboxChange("includeNonMembers")}
                    color="primary"
                    disabled={!recipients.includeMembers}
                  />
                }
                label="Include Non-Members"
              />
            </div> */}

          <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recipients.includeEblastSubscribers}
                  onChange={handleCheckboxChange("includeEblastSubscribers")}
                  color="primary"
                />
              }
              label="E-Blast Subscribers"
            />
          </div>

          {/* <div>
            <FormControlLabel
              control={
                <Checkbox
                  checked={recipients.includeEblastSubscribers}
                  onChange={handleCheckboxChange("includeEblastSubscribers")}
                  color="primary"
                />
              }
              label="E-Blast Subscribers"
            />
          </div> */}
        </div>
      </div>

      <div className="mb-6">
        <p className="text-sm font-medium text-gray-700 mb-2">
          Specific Member Groups:
        </p>

        <Autocomplete
          multiple
          id="member-groups"
          options={memberGroups.map((group) => group.id)}
          getOptionLabel={(option) =>
            memberGroups.find((group) => group.id === option)?.name || ""
          }
          value={recipients.specificGroups}
          onChange={handleGroupsChange}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              placeholder="Select specific groups"
              error={!!errors.recipients}
              helperText={errors.recipients}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "white",
                  borderRadius: 3,
                  "& fieldset": {
                    borderColor: "#B0B5BC",
                  },
                  "&:hover fieldset": {
                    borderColor: "#B0B5BC",
                  },
                  "&.Mui-focused fieldset": {
                    borderWidth: 1,
                    borderColor: "#E1A731",
                  },
                  "&.Mui-error fieldset": {
                    borderColor: "#d32f2f",
                  },
                },
                "& .MuiInputBase-input": {
                  fontSize: 16,
                },
              }}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                label={memberGroups.find((group) => group.id === option)?.name}
                {...getTagProps({ index })}
                key={option}
              />
            ))
          }
        />

        <p className="text-xs text-gray-500 mt-1">
          Select specific member groups to target your email
        </p>
      </div>

      {!recipients.includeMembers &&
        !recipients.includeEblastSubscribers &&
        recipients.specificGroups.length === 0 && (
          <p className="text-sm text-amber-600">
            Please select at least one recipient group
          </p>
        )}
    </FormSection>
  );
};

export default RecipientSection;
